import { Component, OnInit, OnDestroy, signal, effect, HostBinding, Input, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, HostListener, ViewChild, ElementRef, Renderer2, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MD3MenuDirective, MD3MenuTriggerDirective } from '../../../directives/md3-menu.directive';
import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../core/services/theme.service';
import { LoginFormComponent } from '../login-form/login-form.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MD3MenuDirective,
    MD3MenuTriggerDirective,
    LoginFormComponent
  ],
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  // ⚡ OPTIMIZATION: OnPush change detection - safe with signals
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Output() loginRequest = new EventEmitter<void>();
  @ViewChild('userMenu', { read: MD3MenuDirective }) userMenuDirective!: MD3MenuDirective;
  @ViewChild('langMenu', { read: MD3MenuDirective }) langMenuDirective!: MD3MenuDirective;

  // State signals
  mobileMenuOpen = signal(false);
  userMenuOpen = signal(false);
  languageMenuOpen = signal(false);
  currentLanguage = signal('tr');
  loginModalOpen = signal(false);
  scrollState = signal<'hero' | 'default' | 'elevated' | 'compact' | 'ultra-compact'>('default');
  scrollProgress = signal(0);
  isOverHero = signal(false);

  private scrollTimeout: any;
  private clickOutsideListener: any;

  // Inject ThemeService
  private themeService = inject(ThemeService);

  constructor(
    private router: Router,
    public authService: AuthService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    // Track auth service changes
    effect(() => {
      const profile = this.authService.userProfile();
      console.log('Header - User profile changed:', profile);
    });

    // ThemeService initializes theme automatically
  }

  ngOnInit(): void {
    // Initialize language from localStorage or default
    const storedLang = localStorage.getItem('selectedLanguage') || 'tr';
    this.currentLanguage.set(storedLang);

    // Check initial scroll position
    this.onWindowScroll();

    // Set initial header state
    const header = this.elementRef.nativeElement.querySelector('.md3-header');
    if (header) {
      header.setAttribute('data-scroll', this.scrollState());
    }

    // Add click outside listener
    this.clickOutsideListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const header = this.elementRef.nativeElement;

      // Check if click is outside dropdowns
      if (!header.contains(target)) {
        this.closeAllMenus();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.clickOutsideListener) {
      this.clickOutsideListener();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Calculate scroll progress (0-1)
    const maxScroll = documentHeight - windowHeight;
    const progress = Math.min(scrollTop / maxScroll, 1);
    this.scrollProgress.set(progress);

    // Check if header is over hero section (first 600px)
    const heroHeight = 600;
    this.isOverHero.set(scrollTop < heroHeight - 88);

    // Enhanced scroll state management with smooth thresholds
    if (scrollTop === 0) {
      this.scrollState.set(this.isOverHero() ? 'hero' : 'default');
    } else if (scrollTop > 400) {
      this.scrollState.set('ultra-compact');
    } else if (scrollTop > 200) {
      this.scrollState.set('compact');
    } else if (scrollTop > 80) {
      this.scrollState.set('elevated');
    } else if (scrollTop > 20) {
      this.scrollState.set('default');
    } else {
      this.scrollState.set(this.isOverHero() ? 'hero' : 'default');
    }

    // Update header attribute for CSS
    const header = this.elementRef.nativeElement.querySelector('.md3-header');
    if (header) {
      header.setAttribute('data-scroll', this.scrollState());
      header.style.setProperty('--scroll-progress', progress.toString());
    }

    // Clear existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Reset after scrolling stops with spring effect
    this.scrollTimeout = setTimeout(() => {
      if (scrollTop === 0) {
        this.scrollState.set(this.isOverHero() ? 'hero' : 'default');
      }
    }, 150);
  }

  // Navigation
  navigateTo(route: string): void {
    this.router.navigate([route]);
    this.closeAllMenus();
  }

  isActive(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  // Menu toggles
  toggleMobileMenu(): void {
    this.mobileMenuOpen.update(v => !v);
    this.closeOtherMenus('mobile');
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
    this.closeOtherMenus('user');
  }

  toggleLanguageMenu(): void {
    this.languageMenuOpen.update(v => !v);
    this.closeOtherMenus('language');
  }

  closeOtherMenus(except: string): void {
    if (except !== 'mobile') this.mobileMenuOpen.set(false);
    if (except !== 'user') this.userMenuOpen.set(false);
    if (except !== 'language') this.languageMenuOpen.set(false);
  }

  closeAllMenus(): void {
    this.mobileMenuOpen.set(false);
    this.userMenuOpen.set(false);
    this.languageMenuOpen.set(false);
  }

  // Theme management (3-way toggle: light/system/dark)
  setTheme(mode: 'light' | 'dark' | 'system'): void {
    this.themeService.setThemeMode(mode);
  }

  getCurrentTheme(): 'light' | 'dark' | 'system' {
    return this.themeService.currentThemeMode();
  }

  isDarkMode(): boolean {
    return this.themeService.currentActualTheme() === 'dark';
  }

  // Legacy method for backward compatibility
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  // Language management
  changeLanguage(lang: string): void {
    this.currentLanguage.set(lang);
    localStorage.setItem('selectedLanguage', lang);
    this.languageMenuOpen.set(false);

    // Emit language change event if needed
    // this.languageService.setLanguage(lang);
  }

  // Login modal
  openLoginModal(): void {
    this.loginModalOpen.set(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  }

  closeLoginModal(): void {
    this.loginModalOpen.set(false);
    document.body.style.overflow = ''; // Restore scrolling
  }

  onLoginSuccess(): void {
    this.closeLoginModal();
    // Navigate to dashboard or refresh
    this.router.navigate(['/dashboard']);
  }

  onLoginClick(): void {
    this.loginRequest.emit();
    this.openLoginModal();
  }

  // User management
  getRoleDisplayName(): string {
    const role = this.authService.userProfile()?.role;
    switch(role) {
      case 'ADMIN':
        return 'Yönetici';
      case 'PHYSICIAN':
        return 'Hekim';
      case 'VIEWER':
        return 'Görüntüleyici';
      default:
        return 'Kullanıcı';
    }
  }

  logout(): void {
    this.closeAllMenus();
    this.authService.signOut().then(() => {
      this.router.navigate(['/login']);
    });
  }

  // Keyboard navigation
  @HostListener('keydown.escape')
  onEscapeKey(): void {
    this.closeAllMenus();
    this.closeLoginModal();
  }
}